import { useState } from 'react';
import {
  Card,
  CardBody,
  Input,
  Badge,
  Alert,
  Button,
  ButtonGroup,
  FormGroup,
  Label,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { DataTable } from '../../components/DataTable';
import { RelativeTime } from '../../components/RelativeTime';
import { CopyChip } from '../../components/CopyChip';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { safeExternalUrl } from '../../utils/safeUrl';
import {
  useMailSecurityList,
  useMailSecurityDetail,
  useMailSecurityPreview,
  useRotateDkim,
  useReapplyMailSecurity,
  useUploadBimiLogo,
  useSetVmc,
} from './useNexusMailSecurity';

const DMARC_COLORS = { none: 'secondary', quarantine: 'warning', reject: 'danger' };

/**
 * Nexus Mail-Security Übersicht (Spec 6 von 8).
 *
 * Spec: lmm-wiki/wiki/orbit-specs/nexus-mail-security-overview.md
 * Endpoints: /v1/admin/mail-security
 *
 * Operator-View nicht Customer-facing. DKIM-Rotate + Re-Apply als
 * operative Aktionen. BIMI-Upload + VMC-Replace folgen sobald die
 * passenden Multipart-Endpoints klar dokumentiert sind.
 */
export function NexusMailSecurityPage() {
  const [domainFilter, setDomainFilter] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [openDomain, setOpenDomain] = useState(null);

  const query = useMailSecurityList({ domainFilter, tenantId });
  const records = query.data?.records ?? [];

  return (
    <div>
      <PageHeader
        title="Nexus · Mail-Security"
        subtitle={`${records.length} Domains · DKIM/SPF/DMARC/BIMI Status`}
      />

      <Alert color="info" className="small">
        Operator-Sicht — Customer-UI für BIMI/DMARC-Reports lebt weiterhin
        in <code>dash.valitype.com</code>. Hier nur Triage-Aktionen:
        DKIM-Rotation, Re-Apply nach Provider-Wechsel.
      </Alert>

      <Card className="mb-3">
        <CardBody className="py-2">
          <div className="d-flex flex-wrap gap-3 align-items-end">
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Domain</Label>
              <Input type="text" bsSize="sm" value={domainFilter} onChange={(e) => setDomainFilter(e.target.value)} placeholder="Filter…" style={{ minWidth: 200 }} />
            </FormGroup>
            <FormGroup className="mb-0">
              <Label className="small text-muted mb-1">Tenant-ID</Label>
              <Input type="text" bsSize="sm" value={tenantId} onChange={(e) => setTenantId(e.target.value)} placeholder="leer = alle" style={{ minWidth: 120 }} />
            </FormGroup>
          </div>
        </CardBody>
      </Card>

      <DataTable
        loading={query.isLoading}
        error={query.error}
        empty={!query.isLoading && records.length === 0}
        emptyMessage="Keine Domains für die aktuellen Filter."
        onRetry={() => query.refetch()}
      >
        <thead>
          <tr>
            <th>Domain</th>
            <th>Account</th>
            <th style={{ width: 160 }}>DKIM-Selector</th>
            <th style={{ width: 130 }}>DMARC</th>
            <th style={{ width: 80 }} className="text-center">BIMI</th>
            <th style={{ width: 80 }} className="text-center">VMC</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r) => (
            <tr key={r.domain} role="button" onClick={() => setOpenDomain(r.domain)} style={{ cursor: 'pointer' }}>
              <td className="fw-semibold">{r.domain}</td>
              <td className="small font-monospace">{r.account ?? <span className="text-muted">—</span>}</td>
              <td><code className="small">{r.dkim_selector ?? '—'}</code></td>
              <td>
                <Badge color={DMARC_COLORS[r.dmarc_policy] ?? 'secondary'} pill>
                  {r.dmarc_policy ?? '—'}
                </Badge>
              </td>
              <td className="text-center">
                {r.bimi_active ? <Badge color="success" pill>✓</Badge> : <span className="text-muted">—</span>}
              </td>
              <td className="text-center">
                {r.vmc_url ? <Badge color="success" pill>✓</Badge> : <span className="text-muted">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>

      <DomainDetailDrawer
        domain={openDomain}
        isOpen={!!openDomain}
        onClose={() => setOpenDomain(null)}
      />
    </div>
  );
}

function DomainDetailDrawer({ domain, isOpen, onClose }) {
  const detail = useMailSecurityDetail(domain);
  const preview = useMailSecurityPreview(domain);
  const rotate = useRotateDkim();
  const reapply = useReapplyMailSecurity();
  const uploadLogo = useUploadBimiLogo();
  const setVmc = useSetVmc();
  const canWrite = useHasPermission({ perms: ['integrations.write'] });

  const [pendingRotate, setPendingRotate] = useState(false);
  const [reapplyOpen, setReapplyOpen] = useState(false);
  const [bimiOpen, setBimiOpen] = useState(false);
  const [vmcOpen, setVmcOpen] = useState(false);
  const [pendingVmcRemove, setPendingVmcRemove] = useState(false);

  const record = detail.data;

  return (
    <>
      <Offcanvas isOpen={isOpen} toggle={onClose} direction="end" style={{ width: 640 }}>
        <OffcanvasHeader toggle={onClose}>
          Mail-Security · {domain}
        </OffcanvasHeader>
        <OffcanvasBody>
          {detail.isLoading && <p>Lade…</p>}
          {detail.isError && (
            <Alert color="danger">Detail konnte nicht geladen werden: {detail.error?.message}</Alert>
          )}
          {record && (
            <>
              <h6 className="text-uppercase small text-muted mt-1">SPF</h6>
              <CopyChip value={record.spf ?? '—'} label="SPF-Record" />

              <h6 className="text-uppercase small text-muted mt-3">DMARC</h6>
              <CopyChip value={record.dmarc ?? '—'} label="DMARC-Record" />
              <div className="small text-muted mt-1">
                Policy: <Badge color={DMARC_COLORS[record.dmarc_policy] ?? 'secondary'} pill>{record.dmarc_policy ?? '—'}</Badge>
              </div>

              <h6 className="text-uppercase small text-muted mt-3">DKIM</h6>
              <dl className="row small">
                <dt className="col-4 text-muted">Aktiver Selector</dt>
                <dd className="col-8"><code>{record.dkim_selector}</code></dd>
                <dt className="col-4 text-muted">Algo</dt>
                <dd className="col-8"><code>{record.dkim_algo}</code></dd>
              </dl>

              {record.dkim_history && record.dkim_history.length > 0 && (
                <>
                  <div className="small fw-semibold mt-2">Historie</div>
                  <table className="table table-sm small">
                    <thead>
                      <tr><th>Selector</th><th>Algo</th><th>Erstellt</th><th>Status</th></tr>
                    </thead>
                    <tbody>
                      {record.dkim_history.map((h) => (
                        <tr key={h.selector}>
                          <td><code>{h.selector}</code></td>
                          <td><code>{h.algo}</code></td>
                          <td><RelativeTime iso={h.created_at} /></td>
                          <td>
                            {h.status === 'active' ? (
                              <Badge color="success" pill>active</Badge>
                            ) : (
                              <Badge color="secondary" pill className="orbit-code-badge-sm">{h.status}</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}

              <h6 className="text-uppercase small text-muted mt-3 d-flex justify-content-between align-items-center">
                <span>BIMI</span>
                <HasPermission perms={['integrations.write']}>
                  {canWrite && (
                    <Button color="primary" outline size="sm" onClick={() => setBimiOpen(true)} disabled={uploadLogo.isPending}>
                      🖼️ Logo ersetzen
                    </Button>
                  )}
                </HasPermission>
              </h6>
              {record.bimi_active ? (
                <dl className="row small">
                  <dt className="col-4 text-muted">Logo</dt>
                  <dd className="col-8">
                    {safeExternalUrl(record.bimi_logo_url) ? (
                      <a href={safeExternalUrl(record.bimi_logo_url)} target="_blank" rel="noopener noreferrer" className="small">
                        {record.bimi_logo_url}
                      </a>
                    ) : (
                      <code className="small">{record.bimi_logo_url}</code>
                    )}
                    {record.bimi_logo_bytes && (
                      <span className="text-muted small ms-2">
                        ({Math.round(record.bimi_logo_bytes / 1024)} KB)
                      </span>
                    )}
                  </dd>
                </dl>
              ) : (
                <p className="small text-muted">BIMI-Logo noch nicht gesetzt.</p>
              )}

              <h6 className="text-uppercase small text-muted mt-3 d-flex justify-content-between align-items-center">
                <span>VMC (Verified Mark Certificate)</span>
                <HasPermission perms={['integrations.write']}>
                  {canWrite && (
                    <div className="d-flex gap-1">
                      <Button color="primary" outline size="sm" onClick={() => setVmcOpen(true)} disabled={setVmc.isPending}>
                        {record.vmc_url ? '🔁 Ersetzen' : '+ Setzen'}
                      </Button>
                      {record.vmc_url && (
                        <Button color="danger" outline size="sm" onClick={() => setPendingVmcRemove(true)} disabled={setVmc.isPending}>
                          🗑️ Entfernen
                        </Button>
                      )}
                    </div>
                  )}
                </HasPermission>
              </h6>
              {record.vmc_url ? (
                safeExternalUrl(record.vmc_url) ? (
                  <a href={safeExternalUrl(record.vmc_url)} target="_blank" rel="noopener noreferrer" className="small d-block text-break">
                    {record.vmc_url}
                  </a>
                ) : (
                  <code className="small d-block text-break">{record.vmc_url}</code>
                )
              ) : (
                <p className="small text-muted mb-0">Kein VMC hinterlegt — BIMI-Logo wird in unterstützenden Mail-Clients ohne Mark-Indikator angezeigt.</p>
              )}

              <h6 className="text-uppercase small text-muted mt-4">Vorschau (Diff)</h6>
              <PreviewBlock query={preview} />


              {/* last_apply_at / last_apply_job_id existieren nicht in mail_security_records
                  (Audit B8) — Block entfernt. Audit-Trail kommt via /v1/admin/audit
                  sobald Recent-Changes-Panels gebaut werden. */}

              <HasPermission perms={['integrations.write']}>
                {canWrite && (
                  <div className="mt-4 pt-3 border-top">
                    <h6 className="text-uppercase small text-muted">DNS-Aktionen</h6>
                    <ButtonGroup className="d-flex">
                      <Button color="warning" outline onClick={() => setPendingRotate(true)} disabled={rotate.isPending}>
                        🔁 DKIM rotieren
                      </Button>
                      <Button color="info" outline onClick={() => setReapplyOpen(true)} disabled={reapply.isPending}>
                        🔧 Re-Apply
                      </Button>
                    </ButtonGroup>
                  </div>
                )}
              </HasPermission>
            </>
          )}
        </OffcanvasBody>
      </Offcanvas>

      <ConfirmDialog
        isOpen={pendingRotate}
        title={`DKIM-Rotation für ${domain} starten?`}
        body={
          <>
            <p className="mb-2">
              Neuer Selector wird erzeugt. Alter Selector bleibt für ein
              Grace-Window aktiv (aktuell hartes Flip).
            </p>
            <p className="mb-0 small text-muted">
              Vorhandene Mail-Server müssen den neuen Selector kennen — Re-Apply
              wird empfohlen.
            </p>
          </>
        }
        confirmLabel="Rotieren"
        confirmColor="warning"
        busy={rotate.isPending}
        onConfirm={async () => {
          await rotate.mutateAsync({ domain });
          setPendingRotate(false);
        }}
        onCancel={() => setPendingRotate(false)}
      />

      {bimiOpen && (
        <BimiLogoUploadModal
          domain={domain}
          onClose={() => setBimiOpen(false)}
          mutation={uploadLogo}
        />
      )}

      {vmcOpen && (
        <VmcModal
          domain={domain}
          currentUrl={record?.vmc_url ?? ''}
          onClose={() => setVmcOpen(false)}
          mutation={setVmc}
        />
      )}

      <ConfirmDialog
        isOpen={pendingVmcRemove}
        title={`VMC für ${domain} entfernen?`}
        body="Backend ruft PUT /vmc mit vmc_url=null auf. BIMI-Logo bleibt erhalten, nur der Mark-Indikator entfällt."
        confirmLabel="Entfernen"
        confirmColor="danger"
        busy={setVmc.isPending}
        onConfirm={async () => {
          await setVmc.mutateAsync({ domain, vmc_url: null });
          setPendingVmcRemove(false);
        }}
        onCancel={() => setPendingVmcRemove(false)}
      />

      {reapplyOpen && (
        <ReapplyModal
          domain={domain}
          onClose={() => setReapplyOpen(false)}
          mutation={reapply}
        />
      )}
    </>
  );
}

function ReapplyModal({ domain, onClose, mutation }) {
  const [accountId, setAccountId] = useState('');
  const [primaryIp, setPrimaryIp] = useState('');
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      // primary_ip leer → nicht senden, Nexus löst via hosting.getPrimaryIp(account_id) auf.
      const body = { domain, account_id: accountId };
      if (primaryIp.trim()) body.primary_ip = primaryIp.trim();
      await mutation.mutateAsync(body);
      onClose();
    } catch (err) {
      setError(err?.data?.message ?? err?.message ?? 'Re-Apply fehlgeschlagen.');
    }
  };

  return (
    <Modal isOpen toggle={mutation.isPending ? undefined : onClose}>
      <Form onSubmit={onSubmit}>
        <ModalHeader toggle={mutation.isPending ? undefined : onClose}>
          Mail-Security Re-Apply · {domain}
        </ModalHeader>
        <ModalBody>
          <Alert color="info" className="small">
            Enqueued einen <code>mail_security_apply</code>-Job. SPF/DKIM/DMARC
            werden gegen den angegebenen Account neu gesetzt.
          </Alert>
          <FormGroup>
            <Label>Account-ID *</Label>
            <Input
              type="text"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="z.B. enhance-org_42"
              required
            />
            <div className="small text-muted mt-1">
              Akzeptiert <code>&lt;orgId&gt;</code>, <code>org:&lt;orgId&gt;</code>,
              <code> &lt;orgId&gt;/&lt;websiteId&gt;</code> oder <code>website:&lt;id&gt;</code>.
            </div>
          </FormGroup>
          <FormGroup className="mb-0">
            <Label>Primary-IP</Label>
            <Input
              type="text"
              value={primaryIp}
              onChange={(e) => setPrimaryIp(e.target.value)}
              placeholder="leer = automatisch via Plugin ermitteln"
            />
            <div className="small text-muted mt-1">
              Optional — leer lassen damit Nexus die IP über
              <code> hosting.getPrimaryIp(account_id) </code>
              auflöst. Nur überschreiben wenn der Operator eine konkrete IP erzwingen will.
            </div>
          </FormGroup>
          {error && <Alert color="danger" className="mt-2 mb-0 small">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={mutation.isPending}>
            Abbrechen
          </Button>
          <Button color="primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Enqueue läuft…' : 'Re-Apply enqueuen'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

/**
 * Preview-Diff-Block. Lädt /preview und rendert suggestions colored.
 * State pro Record:
 *   - 'ok'      → grün
 *   - 'drift'   → gelb mit from→to
 *   - 'missing' → blau
 */
function PreviewBlock({ query }) {
  if (query.isLoading) return <div className="small text-muted">Lade Vorschau…</div>;
  if (query.isError) {
    return <Alert color="warning" className="small mb-0">Preview konnte nicht geladen werden: {query.error?.message}</Alert>;
  }
  const data = query.data;
  if (!data) return <div className="small text-muted">Keine Vorschau-Daten.</div>;
  const suggestions = data.suggestions ?? [];
  if (suggestions.length === 0) {
    return (
      <Alert color="success" className="small mb-0">
        ✓ Alle Records stimmen mit dem Soll überein. Reapply würde nichts ändern.
      </Alert>
    );
  }
  return (
    <>
      <div className="small text-muted mb-2 d-flex flex-wrap align-items-center gap-2">
        <span>
          State: <Badge color={data.state === 'ok' ? 'success' : 'warning'} pill>{data.state ?? '—'}</Badge>
        </span>
        {Array.isArray(data.operations) && data.operations.length > 0 && (
          <span>· Operations: <code>{data.operations.join(', ')}</code></span>
        )}
        <span className="ms-auto small">
          Legende:{' '}
          <Badge color="success" pill className="orbit-code-badge-sm">ok</Badge>
          <span className="text-muted ms-1 me-2">passt zum Soll</span>
          <Badge color="warning" pill className="orbit-code-badge-sm">drift</Badge>
          <span className="text-muted ms-1 me-2">weicht ab (Reapply würde fixen)</span>
          <Badge color="info" pill className="orbit-code-badge-sm">missing</Badge>
          <span className="text-muted ms-1">fehlt im DNS</span>
        </span>
      </div>
      <ul className="list-unstyled small mb-0">
        {suggestions.map((s, i) => <PreviewItem key={`${s.record}-${i}`} item={s} />)}
      </ul>
    </>
  );
}

function PreviewItem({ item }) {
  const meta = previewMeta(item.state);
  return (
    <li className={`d-flex gap-2 mb-1 px-2 py-1 rounded bg-${meta.bg}-subtle`}>
      <span style={{ minWidth: 60 }}>
        <Badge color={meta.color} pill className="orbit-code-badge-sm">{item.record}</Badge>
      </span>
      <span className="flex-grow-1">
        <span className={`fw-semibold text-${meta.color}-emphasis`}>{meta.label}</span>
        {item.state === 'drift' && (
          <div className="small text-muted mt-1">
            <code className="text-danger">{truncate(item.from)}</code>
            <span className="mx-1">→</span>
            <code className="text-success">{truncate(item.to)}</code>
          </div>
        )}
        {item.message && <div className="small text-muted">{item.message}</div>}
      </span>
    </li>
  );
}

function previewMeta(state) {
  if (state === 'ok') return { color: 'success', bg: 'success', label: 'OK' };
  if (state === 'drift') return { color: 'warning', bg: 'warning', label: 'Drift' };
  if (state === 'missing') return { color: 'info', bg: 'info', label: 'Missing' };
  return { color: 'secondary', bg: 'secondary', label: state ?? 'unknown' };
}

function truncate(s, max = 80) {
  if (typeof s !== 'string') return JSON.stringify(s);
  return s.length > max ? s.slice(0, max) + '…' : s;
}

/**
 * BIMI-Logo Upload — Multipart-Form. Backend normalisiert zu SVG-Tiny 1.2.
 * Frontend prüft nur File-Größe (1.5 MB Hard-Limit per Spec) clientseitig
 * damit der User nicht erst 30 Sek wartet bis der Server ablehnt.
 */
function BimiLogoUploadModal({ domain, onClose, mutation }) {
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      setError('Bitte eine Datei wählen.');
      return;
    }
    if (file.size > 1.5 * 1024 * 1024) {
      setError('Datei zu groß — Limit 1.5 MB. Backend reduziert zu SVG-Tiny.');
      return;
    }
    try {
      await mutation.mutateAsync({ domain, file });
      onClose();
    } catch (err) {
      setError(err?.data?.message ?? err?.message ?? 'Upload fehlgeschlagen.');
    }
  };

  return (
    <Modal isOpen toggle={mutation.isPending ? undefined : onClose}>
      <Form onSubmit={onSubmit}>
        <ModalHeader toggle={mutation.isPending ? undefined : onClose}>
          BIMI-Logo · {domain}
        </ModalHeader>
        <ModalBody>
          <Alert color="info" className="small">
            PNG / JPEG / SVG akzeptiert. Backend normalisiert zu SVG-Tiny 1.2
            (≤ 32 KB), uploaded nach S3 und persistiert <code>bimi_logo_url</code>.
            Hard-Limit clientseitig: 1.5 MB.
          </Alert>
          <FormGroup className="mb-0">
            <Label>Logo-Datei *</Label>
            <Input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setError(null);
              }}
              disabled={mutation.isPending}
              required
            />
            {file && (
              <div className="small text-muted mt-1">
                {file.name} · {Math.round(file.size / 1024)} KB
              </div>
            )}
          </FormGroup>
          {error && <Alert color="danger" className="mt-2 mb-0 small">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={mutation.isPending}>
            Abbrechen
          </Button>
          <Button color="primary" type="submit" disabled={mutation.isPending || !file}>
            {mutation.isPending ? 'Upload läuft…' : 'Logo hochladen'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}

/**
 * VMC URL setzen/ersetzen. Backend lädt PEM, prüft Ablauf / SAN / Issuer.
 * Antwort kann warnings[] enthalten — Toast-Message zeigt sie an.
 */
function VmcModal({ domain, currentUrl, onClose, mutation }) {
  const [url, setUrl] = useState(currentUrl);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!url.trim()) {
      setError('URL ist Pflicht — zum Entfernen den Entfernen-Button nutzen.');
      return;
    }
    try {
      await mutation.mutateAsync({ domain, vmc_url: url.trim() });
      onClose();
    } catch (err) {
      setError(err?.data?.message ?? err?.message ?? 'VMC-Setzung fehlgeschlagen.');
    }
  };

  return (
    <Modal isOpen toggle={mutation.isPending ? undefined : onClose}>
      <Form onSubmit={onSubmit}>
        <ModalHeader toggle={mutation.isPending ? undefined : onClose}>
          VMC · {domain}
        </ModalHeader>
        <ModalBody>
          <Alert color="info" className="small">
            Backend lädt das PEM, prüft Ablauf, SAN (muss Domain enthalten)
            und Issuer-Allowlist hart. Warnungen werden zurückgegeben aber
            nicht blockierend.
          </Alert>
          <FormGroup className="mb-0">
            <Label>VMC PEM-URL *</Label>
            <Input
              type="url"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setError(null);
              }}
              placeholder="https://vmc.example.com/cert.pem"
              disabled={mutation.isPending}
              required
            />
          </FormGroup>
          {error && <Alert color="danger" className="mt-2 mb-0 small">{error}</Alert>}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={onClose} disabled={mutation.isPending}>
            Abbrechen
          </Button>
          <Button color="primary" type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? 'Validierung läuft…' : 'Validieren & speichern'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
}
